import { Route, Routes } from "react-router-dom";
import Home from "./routes/Home";
import SignUp from "./routes/auth/SignUp";
import Login from "./routes/auth/Login";
import ResetPassword from "./routes/auth/ResetPassword";
import NewPassword from "./routes/auth/NewPassword";
import Maintenance from "./routes/maintenance/Maintenance";
import PageNotFound from "./routes/404/PageNotFound";
import PwaTutorial from "./routes/pwa-tutorial/PwaTutorial";
import Dashboard from "./routes/dashboard/Dashboard";
import HomePage from "./components/dashboard/HomePage";
import BuyAirtime from "./routes/dashboard/buy-airtime/BuyAirtime";
import BuyData from "./routes/dashboard/buy-data/BuyData";
import BuyElectricity from "./routes/dashboard/buy-electricity/BuyElectricity";
import BuyCable from "./routes/dashboard/buy-cable/BuyCable";
import BuyExamPins from "./routes/dashboard/buy-exam-pins/BuyExamPins";
import OrderHistory from "./routes/dashboard/buy-exam-pins/OrderHistory";
import FundWallet from "./routes/dashboard/fund-wallet/FundWallet";
import Transactions from "./routes/dashboard/transactions/Transactions";
import Settings from "./routes/dashboard/settings/Settings";
import { useMediaQuery } from "react-responsive";
import { Toaster } from "sonner";
import { Provider } from "react-redux";
import store from "./redux/store";
import PaymentSuccessful from "./routes/payment-successful/PaymentSuccessful";
import VerifyEmail from "./routes/auth/VerifyEmail";
import Oauth from "./routes/oauth/Oauth";
import Beneficiary from "./routes/dashboard/beneficiary/Beneficiary";
import { useEffect } from "react";
import { StatusBar, Style } from "@capacitor/status-bar";
import { Capacitor } from "@capacitor/core";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";
import { App as CapacitorApp } from "@capacitor/app";
import { useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";

let prev = null;
const App = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const platform = Capacitor.getPlatform();
  Keyboard.setResizeMode({ mode: KeyboardResize.Body });

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handler = CapacitorApp.addListener("backButton", ({ canGoBack }) => {
      // Check if we should go back in app navigation
      if (location.pathname !== "/" && canGoBack !== false) {
        navigate(-1); // Go back one step in history
      } else {
        CapacitorApp.exitApp(); // Exit if we're at root
      }
    });

    return () => {
      handler?.remove();
    };
  }, [location, navigate]);

  useEffect(() => {
    if (platform != "web") {
      const interval = setInterval(async () => {
        const theme = localStorage.getItem("theme");
        if (prev != theme) {
          await StatusBar.setStyle({
            style: theme == "dark" ? Style.Dark : Style.Light,
          });
          prev = theme;
        }
      }, 500);

      return () => clearInterval(interval);
    }
  }, [platform]);

  useEffect(() => {
    if (platform !== "web") {
      const handleFocus = (e) => {
        if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") {
          const rect = e.target.getBoundingClientRect();
          const distanceToBottom = Math.abs(window.innerHeight - rect.bottom);
          // console.log(distanceToBottom);
          if (distanceToBottom <= window.innerHeight / 2.5) {
            document.body.style.paddingBottom = `${Math.min(distanceToBottom, 200)}px`;
            e.target.scrollIntoView({ behavior: "smooth" });
          }
        }
      };

      const handleBlur = () => {
        document.body.style.paddingBottom = "0px";
      };
      Keyboard.addListener("keyboardWillHide", handleBlur);

      document.body.addEventListener("focusin", handleFocus);
      document.body.addEventListener("focusout", handleBlur);

      return () => {
        document.body.removeEventListener("focusin", handleFocus);
        document.body.removeEventListener("focusout", handleBlur);
        Keyboard.removeAllListeners();
      };
    }
  }, [platform]);

  return (
    <main>
      <Provider store={store}>
        <Toaster
          richColors
          position={`${isMobile ? "top-right" : "bottom-right"}`}
          duration={3000}
          toastOptions={{
            className: "font",
            // style: {
            //   alignItems: "start",
            // },
          }}
        />
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<Home />} />
            <Route path="/sign-up" element={<SignUp />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/create-password" element={<NewPassword />} />
            <Route path="/verify-email/:code" element={<VerifyEmail />} />
            <Route path="/oauth/:token" element={<Oauth />} />

            <Route path="/pwa-tutorial" element={<PwaTutorial />} />
            <Route path="/payment-successful" element={<PaymentSuccessful />} />
            <Route path="/maintenance" element={<Maintenance />} />
            <Route path="*" element={<PageNotFound />} />

            <Route path="/dashboard" element={<Dashboard />}>
              <Route index element={<HomePage />} />
              <Route path="buy-airtime" element={<BuyAirtime />} />
              <Route path="buy-data" element={<BuyData />} />
              <Route
                path="buy-electricity-bills"
                element={<BuyElectricity />}
              />
              <Route path="buy-cable-subscription" element={<BuyCable />} />
              <Route path="buy-exam-pins" element={<BuyExamPins />} />
              <Route path="order-history" element={<OrderHistory />} />
              <Route path="beneficiaries" element={<Beneficiary />} />

              <Route path="fund-wallet" element={<FundWallet />} />
              <Route path="transactions" element={<Transactions />} />
              <Route path="account" element={<Settings />} />
            </Route>
          </Routes>
        </AnimatePresence>
      </Provider>
    </main>
  );
};

export default App;
